import React from 'react';
import { Plus, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { SocialIcon, type SocialPlatform } from '../icons/SocialIcon';

export type PostingFrequency = 'daily' | 'weekly' | 'bi-weekly' | 'monthly';

export interface SocialNetwork {
  platform: string;
  frequency: PostingFrequency;
}

interface SocialFrequencyDisplayProps {
  networks: SocialNetwork[];
  onAddNetwork?: () => void;
  onDeleteNetwork?: (platform: string) => void;
  showAddButton?: boolean;
  isPersonaForm?: boolean;
}

// Helper function to normalize platform names to match SocialPlatform type
const normalizePlatformName = (platform: string): SocialPlatform | null => {
  // Convert to lowercase and remove any spaces or special characters
  const normalized = platform.toLowerCase().trim();
  
  // Map of possible API responses to valid SocialPlatform values
  const platformMap: Record<string, SocialPlatform> = {
    'linkedin': 'linkedin',
    'facebook': 'facebook',
    'instagram': 'instagram',
    'twitter': 'twitter',
    'youtube': 'youtube',
    'tiktok': 'tiktok',
    'pinterest': 'pinterest',
    'google business': 'google-business',
    'google-business': 'google-business',
    'googlebusiness': 'google-business'
  };
  
  return platformMap[normalized] || null;
};

export const SocialFrequencyDisplay: React.FC<SocialFrequencyDisplayProps> = ({
  networks = [],
  onAddNetwork,
  onDeleteNetwork,
  showAddButton = true,
  isPersonaForm = false
}) => {
  const { t } = useTranslation();
  
  const getFrequencyLabel = (frequency: PostingFrequency) => {
    switch (frequency) {
      case 'daily':
        return 'Quotidien';
      case 'weekly':
        return 'Hebdomadaire';
      case 'bi-weekly':
        return 'Bi-hebdomadaire';
      case 'monthly':
        return 'Mensuel';
      default:
        return frequency;
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-gray-700">
          {isPersonaForm 
            ? t('personas.form.socialNetworks') 
            : t('brands.edit.brandGuidelines.recommendedSocialNetworks')}
        </h3>
        {showAddButton && onAddNetwork && (
          <button
            onClick={onAddNetwork}
            className="inline-flex items-center text-sm text-primary-600 hover:text-primary-700"
          >
            <Plus className="w-4 h-4 mr-1" />
            {t('common.add')}
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {Array.isArray(networks) && networks.map((network, index) => {
          // Normalize the platform name to match SocialPlatform type
          const normalizedPlatform = normalizePlatformName(network.platform);
          
          // Only render if we have a valid platform
          if (!normalizedPlatform) return null;
          
          return (
            <div
              key={index}
              className="flex flex-col items-center p-4 bg-white rounded-lg relative group"
            >
              {onDeleteNetwork && (
                <button
                  onClick={() => onDeleteNetwork(network.platform)}
                  className="absolute -top-2 -right-2 p-1 bg-white rounded-full border border-gray-200 shadow-sm opacity-0 group-hover:opacity-100 transition-opacity hover:border-red-300"
                >
                  <X className="w-3 h-3 text-red-500" />
                </button>
              )}
              <SocialIcon platform={normalizedPlatform} size={24} />
              <span className="mt-2 text-xs text-gray-600">
                {getFrequencyLabel(network.frequency)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};